import type { CalendarOccurrence } from '../../calendar-domain.js';
import type { CalendarColumnIds } from '../../calendar-view-manager.js';
import { writeWithLiveCalendarColumns } from '../calendar-event-store.js';
import type { CalendarEvent } from '../calendar-view-types.js';
import type { CalendarEventInteractionContext } from './calendar-event-interactions.js';

type CalendarClipboardEntry = {
  event: CalendarEvent;
  mode: 'copy' | 'cut';
  removeSource?: () => void;
};

let calendarClipboard: CalendarClipboardEntry | undefined;

function cloneEvent(event: CalendarEvent): CalendarEvent {
  return {
    ...event,
    endAt: new Date(event.endAt),
    startAt: new Date(event.startAt),
  };
}

export function calendarClipboardHasEvent(): boolean {
  return calendarClipboard !== undefined;
}

export function copyCalendarOccurrence(
  occurrence: CalendarOccurrence<CalendarEvent>
): void {
  calendarClipboard = {
    event: cloneEvent(occurrence.source),
    mode: 'copy',
  };
}

export function cutCalendarOccurrence(
  occurrence: CalendarOccurrence<CalendarEvent>,
  removeSource: () => void
): void {
  calendarClipboard = {
    event: cloneEvent(occurrence.source),
    mode: 'cut',
    removeSource,
  };
}

export function pasteCalendarOccurrence(
  context: CalendarEventInteractionContext
): Promise<string | undefined> {
  const entry = calendarClipboard;
  if (!entry || context.readonly()) return Promise.resolve(undefined);
  return writeWithLiveCalendarColumns(context, columns =>
    pasteCalendarEntry(context, entry, columns)
  );
}

function pasteCalendarEntry(
  context: CalendarEventInteractionContext,
  entry: CalendarClipboardEntry,
  columns: CalendarColumnIds
): string | undefined {
  const rowId = context.addRow();
  if (!rowId) return;
  const event = entry.event;

  context.setCellJson(rowId, columns.titleColumn, event.title);
  context.setCellJson(rowId, columns.startDateCol, event.startAt.getTime());
  context.setCellJson(rowId, columns.endDateCol, event.endAt.getTime());
  context.setCellJson(rowId, columns.allDayCol, event.allDay);
  context.setCellJson(rowId, columns.timezoneCol, event.timezone);
  // Placement is the containing calendar database. A provider calendar
  // identity must never leak from the source into a different destination.
  context.setCellJson(rowId, columns.calendarIdCol, '');
  context.setCellJson(
    rowId,
    columns.locationPhysicalCol,
    event.locationPhysical
  );
  context.setCellJson(rowId, columns.locationVirtualCol, event.locationVirtual);
  context.setCellJson(rowId, columns.attendeesCol, event.attendees);
  context.setCellJson(rowId, columns.reminderCol, event.reminder);
  context.setCellJson(rowId, columns.descriptionCol, event.description);
  context.setCellJson(rowId, columns.recurrenceCol, event.recurrence);
  context.setCellJson(
    rowId,
    columns.recurrenceExdateCol,
    event.recurrenceExdate
  );
  context.setCellJson(rowId, columns.recurrenceIdCol, event.recurrenceId);
  context.setCellJson(rowId, columns.statusCol, event.status);
  context.setCellJson(rowId, columns.visibilityCol, event.visibility);
  context.setSelectedOccurrence(rowId, event.startAt.toISOString());

  if (entry.mode === 'cut') {
    calendarClipboard = undefined;
    entry.removeSource?.();
  }
  return rowId;
}

export function clearCalendarClipboardForTests(): void {
  calendarClipboard = undefined;
}
